// store/slices/invoiceSlice.ts
import {
  invoiceApi,
  InvoiceDetail,
  InvoiceSummary,
} from "@/services/invoiceApi";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

interface InvoiceState {
  invoices: InvoiceSummary[];
  pagination: {
    totalItems: number;
    totalPages: number;
    currentPage: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  selectedInvoice: InvoiceDetail | null;
  loading: boolean;
  error: string | null;
  updating: boolean;
}

const initialState: InvoiceState = {
  invoices: [],
  pagination: {
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false,
  },
  selectedInvoice: null,
  loading: false,
  error: null,
  updating: false,
};

export const fetchInvoices = createAsyncThunk(
  "invoices/fetchAll",
  async ({
    startDate,
    endDate,
    customerId,
    createdBy,
    areaId,
    page = 1,
    limit = 10,
  }: {
    startDate?: string;
    endDate?: string;
    customerId?: number;
    createdBy?: number;
    areaId?: number;
    page?: number;
    limit?: number;
  }) => {
    return await invoiceApi.listInvoices({
      startDate,
      endDate,
      customerId,
      createdBy,
      areaId,
      page,
      limit,
    });
  },
);

export const fetchInvoiceById = createAsyncThunk(
  "invoices/fetchById",
  async (id: number) => {
    return await invoiceApi.getInvoiceById(id);
  },
);

export const createDraftInvoice = createAsyncThunk(
  "invoices/createDraft",
  async (data: Parameters<typeof invoiceApi.createDraft>[0]) => {
    return await invoiceApi.createDraft(data);
  },
);

export const confirmInvoice = createAsyncThunk(
  "invoices/confirm",
  async (invoiceId: number) => {
    const result = await invoiceApi.confirmInvoice(invoiceId);
    return { invoiceId, message: result.message };
  },
);

export const recordPayment = createAsyncThunk(
  "invoices/recordPayment",
  async (data: Parameters<typeof invoiceApi.recordPayment>[0]) => {
    const result = await invoiceApi.recordPayment(data);
    // return { invoiceId: data.invoiceId, amount: data.amount, message: result.message };
  },
);

// Add these action creators to your existing slice
export const updateDraftInvoice = createAsyncThunk(
  "invoices/updateDraft",
  async ({ id, data }: { id: number; data: any }, { rejectWithValue }) => {
    try {
      const response = await invoiceApi.updateInvoice(id, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Update failed");
    }
  },
);

export const revertToDraft = createAsyncThunk(
  "invoices/revertToDraft",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await invoiceApi.revertToDraft(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Revert failed");
    }
  },
);

const invoiceSlice = createSlice({
  name: "invoices",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetSelected: (state) => {
      state.selectedInvoice = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInvoices.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        state.loading = false;
        const { page } = action.meta.arg;
        const { invoices, pagination } = action.payload;

        // Compute missing pagination fields
        const hasNextPage =
          pagination.hasNextPage ??
          pagination.currentPage < pagination.totalPages;
        const hasPrevPage =
          pagination.hasPrevPage ?? pagination.currentPage > 1;

        const newPagination = {
          ...pagination,
          hasNextPage,
          hasPrevPage,
        };

        if (page === 1) {
          state.invoices = invoices;
        } else {
          state.invoices = [...state.invoices, ...invoices];
        }
        state.pagination = newPagination;
      })
      .addCase(fetchInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch invoices";
      })
      .addCase(fetchInvoiceById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchInvoiceById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedInvoice = action.payload;
      })
      .addCase(fetchInvoiceById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Invoice not found";
      })
      .addCase(createDraftInvoice.pending, (state) => {
        state.updating = true;
      })
      .addCase(createDraftInvoice.fulfilled, (state) => {
        state.updating = false;
      })
      .addCase(createDraftInvoice.rejected, (state, action) => {
        state.updating = false;
        state.error = action.error.message || "Failed to create draft";
      })
      .addCase(confirmInvoice.pending, (state) => {
        state.updating = true;
      })
      .addCase(confirmInvoice.fulfilled, (state, action) => {
        state.updating = false;
        if (
          state.selectedInvoice &&
          state.selectedInvoice.id === action.payload.invoiceId
        ) {
          state.selectedInvoice.status = "confirmed";
        }
        const idx = state.invoices.findIndex(
          (i) => i.id === action.payload.invoiceId,
        );
        if (idx !== -1) state.invoices[idx].status = "confirmed";
      })
      .addCase(confirmInvoice.rejected, (state, action) => {
        state.updating = false;
        state.error = action.error.message || "Confirmation failed";
      })
      .addCase(recordPayment.pending, (state) => {
        state.updating = true;
      })
      .addCase(recordPayment.fulfilled, (state) => {
        state.updating = false;
      })
      .addCase(recordPayment.rejected, (state, action) => {
        state.updating = false;
        state.error = action.error.message || "Payment failed";
      });
  },
});

export const { clearError, resetSelected } = invoiceSlice.actions;
export default invoiceSlice.reducer;
